import React, { useEffect, useState, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { fetchGrocery, addGroceryItem, toggleGroceryItem, deleteGroceryItem, clearCheckedItems } from '../api';

const GroceryPanel = forwardRef((props, ref) => {
  const [items, setItems] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    const data = await fetchGrocery();
    setItems(data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 1000);
    return () => clearInterval(t);
  }, [load]);

  // Allow QuickAddPanel to trigger a reload when it adds an item
  useImperativeHandle(ref, () => ({ reload: load }));

  const handleAdd = async (name) => {
    const val = name || inputVal.trim();
    if (!val) { setAdding(false); return; }
    const item = await addGroceryItem(val);
    setItems(prev => [item, ...prev]);
    if (!name) {
      setInputVal('');
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') handleAdd();
    if (e.key === 'Escape') { setAdding(false); setInputVal(''); }
  };

  const handleToggle = async (id, checked) => {
    await toggleGroceryItem(id, !checked);
    setItems(prev => prev.map(i => i.id === id ? {...i, checked: !checked ? 1 : 0} : i));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteGroceryItem(id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const handleClearChecked = async () => {
    await clearCheckedItems();
    setItems(prev => prev.filter(i => !i.checked));
  };

  const unchecked = items.filter(i => !i.checked);
  const checked   = items.filter(i => i.checked);

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <div style={styles.panelHeaderLeft}>
          <span style={styles.panelLabel}>Grocery List</span>
          <span style={{...styles.dot, background: 'var(--accent)', boxShadow: '0 0 6px var(--accent)'}} />
          {unchecked.length > 0 && (
            <span style={styles.badge}>{unchecked.length}</span>
          )}
        </div>
        <div style={styles.headerActions}>
          {checked.length > 0 && (
            <button style={styles.clearBtn} onClick={handleClearChecked}>Clear done</button>
          )}
          <button
            style={styles.addBtn}
            onClick={() => { setAdding(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          >
            + Add
          </button>
        </div>
      </div>

      {adding && (
        <div style={styles.addRow}>
          <input
            ref={inputRef}
            style={styles.addInput}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={handleKey}
            onBlur={() => { if (!inputVal.trim()) setAdding(false); }}
            placeholder="Add item…"
            autoFocus
          />
          <button style={styles.addConfirm} onClick={() => handleAdd()}>Add</button>
        </div>
      )}

      <div style={styles.itemGrid}>
        {unchecked.map(item => (
          <div key={item.id} style={styles.item} onClick={() => handleToggle(item.id, item.checked)}>
            <span style={styles.checkbox}>○</span>
            <span style={styles.itemName}>{item.name}</span>
            <button style={styles.deleteBtn} onClick={e => handleDelete(e, item.id)}>×</button>
          </div>
        ))}
        {checked.map(item => (
          <div key={item.id} style={{...styles.item, ...styles.itemChecked}} onClick={() => handleToggle(item.id, item.checked)}>
            <span style={{...styles.checkbox, color: 'var(--accent3)'}}>✓</span>
            <span style={{...styles.itemName, ...styles.itemNameChecked}}>{item.name}</span>
            <button style={styles.deleteBtn} onClick={e => handleDelete(e, item.id)}>×</button>
          </div>
        ))}
        {items.length === 0 && (
          <p style={styles.empty}>List is empty</p>
        )}
      </div>
    </div>
  );
});

export default GroceryPanel;

const styles = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    height: '100%',
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  panelHeaderLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  panelLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.65rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--text-dimmer)',
  },
  dot: { width: 6, height: 6, borderRadius: '50%' },
  badge: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.65rem',
    background: 'var(--accent)',
    color: '#0d0f14',
    borderRadius: 10,
    padding: '1px 6px',
    fontWeight: 600,
  },
  headerActions: { display: 'flex', gap: 8, alignItems: 'center' },
  addBtn: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.72rem',
    color: 'var(--accent)',
    border: '1px solid var(--accent)',
    borderRadius: 20,
    padding: '4px 12px',
    background: 'transparent',
    letterSpacing: '0.05em',
    cursor: 'pointer',
  },
  clearBtn: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.72rem',
    color: 'var(--text-dim)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    padding: '4px 10px',
    background: 'transparent',
    letterSpacing: '0.05em',
    cursor: 'pointer',
  },
  addRow: { display: 'flex', gap: 8, flexShrink: 0 },
  addInput: {
    flex: 1,
    background: 'var(--surface2)',
    border: '1px solid var(--accent)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text)',
    fontSize: '0.9rem',
    padding: '8px 12px',
    outline: 'none',
    fontFamily: 'var(--font-body)',
  },
  addConfirm: {
    background: 'var(--accent)',
    color: '#0d0f14',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    padding: '8px 16px',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.05em',
  },
  itemGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    overflowY: 'auto',
    flex: 1,
    alignContent: 'flex-start',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '7px 10px',
    background: 'var(--surface2)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    border: '1px solid var(--border)',
    minWidth: 100,
  },
  itemChecked: { opacity: 0.45, border: '1px solid transparent' },
  checkbox: { fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-dim)', flexShrink: 0 },
  itemName: { fontSize: '0.88rem', color: 'var(--text)', flex: 1 },
  itemNameChecked: { textDecoration: 'line-through', color: 'var(--text-dim)' },
  deleteBtn: { color: 'var(--text-dimmer)', fontSize: '1rem', lineHeight: 1, padding: '0 2px', flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer' },
  empty: { fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--text-dimmer)', fontStyle: 'italic', padding: '8px 0' },
};
